import {
  featureNames,
  pluralFeatureNames,
} from "@/src/constants/globalConstants.js";
import { stepOptions } from "@/src/constants/sequenceStepOptions.js";
import { permissionOptions } from "@/src/constants/sequenceConstants.js";
import {
  auditOperationType,
  operationLabels,
  statusLabels,
  fieldLabels,
  entityDisplayNames,
  stepTypeDisplayNames,
  sequenceSettingFields,
  ignoredFields,
  auditEntityTypes,
  auditEntities,
  operationConfig,
  entityTypeMapping,
  entityTypeGrouping,
  detailsFieldMapping,
} from "@/src/constants/auditConstants.js";

export function generateAuditMessage(actionBy, auditData, sequenceName = null) {
  try {
    const { operation_type, entity_type, before, after } = auditData;

    const entityName = getEntityDisplayName(entity_type);
    const operationLabel = operationLabels[operation_type] || operation_type;

    switch (operation_type) {
      case auditOperationType.INSERT:
        return generateInsertMessage(
          actionBy,
          entityName,
          after,
          entity_type,
          sequenceName,
          auditData,
        );

      case auditOperationType.DELETE:
        return generateDeleteMessage(
          actionBy,
          entityName,
          before,
          entity_type,
          sequenceName,
          auditData,
        );

      case auditOperationType.UPDATE:
        return generateUpdateMessage(
          actionBy,
          entityName,
          before,
          after,
          entity_type,
          sequenceName,
          auditData,
        );

      default:
        const journeyReference = getJourneyReference(sequenceName);
        return `<b>${actionBy}</b> ${operationLabel} ${journeyReference}`;
    }
  } catch (error) {
    console.error("An error occurred while generating audit message: ", error);
    return null;
  }
}

function getJourneyReference(sequenceName) {
  try {
    if (sequenceName) {
      return `the ${featureNames.SEQUENCES} '<b>${sequenceName}</b>'`;
    }
    return `the ${featureNames.SEQUENCES}`;
  } catch (error) {
    console.error("An error occurred while getting journey reference: ", error);
    return `the ${featureNames.SEQUENCES}`;
  }
}

function generateInsertMessage(
  actionBy,
  entityName,
  after,
  entityType,
  sequenceName,
  auditData,
) {
  try {
    const journeyReference = getJourneyReference(sequenceName);

    if (entityType === auditEntityTypes.SEQUENCE_EXECUTION) {
      const targetEntityType = after?.entity_type?.toLowerCase();
      const entityMapping = entityTypeMapping[targetEntityType];
      const targetName = entityMapping
        ? entityMapping.singular
        : targetEntityType;
      return `<b>${actionBy}</b> added a <b>${targetName}</b> to ${journeyReference}`;
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP) {
      const stepType = getStepTypeDisplayName(
        after?.step_type,
        after?.automation_type,
      );
      const stepPosition = after?.position || "new";
      return `<b>${actionBy}</b> added the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP_MESSAGES) {
      return generateStepMessageInsertMessage(actionBy, after, auditData);
    }

    if (entityType === auditEntityTypes.SEQUENCE_SETTING) {
      return null;
    }

    if (entityType === "SequenceTag") {
      const tagName = after?.tag;
      if (tagName) {
        return `<b>${actionBy}</b> added tag '<b>${tagName}</b>' to ${journeyReference}`;
      }
      return `<b>${actionBy}</b> added tag to ${journeyReference}`;
    }

    if (entityType === auditEntityTypes.SEQUENCE) {
      const name = after?.name || after?.title || after?.subject;
      if (name) {
        return `<b>${actionBy}</b> created the Sequence '<b>${name}</b>'`;
      }
      return `<b>${actionBy}</b> created the Sequence`;
    }

    const name = after?.name || after?.title || after?.subject;
    if (name) {
      return `<b>${actionBy}</b> created the ${featureNames.SEQUENCES} '<b>${name}</b>'`;
    }
    return `<b>${actionBy}</b> created the ${featureNames.SEQUENCES}`;
  } catch (error) {
    console.error("An error occurred while generating insert message: ", error);
    return null;
  }
}

function generateDeleteMessage(
  actionBy,
  entityName,
  before,
  entityType,
  sequenceName,
  auditData,
) {
  try {
    const journeyReference = getJourneyReference(sequenceName);

    if (entityType === auditEntityTypes.SEQUENCE_EXECUTION) {
      const targetEntityType = before?.entity_type?.toLowerCase();
      const entityMapping = entityTypeMapping[targetEntityType];
      const targetName = entityMapping
        ? entityMapping.singular
        : targetEntityType;
      return `<b>${actionBy}</b> removed a <b>${targetName}</b> from ${journeyReference}`;
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP) {
      const stepType = getStepTypeDisplayName(
        before?.step_type,
        before?.automation_type,
      );
      const stepPosition = before?.position || "unknown";
      return `<b>${actionBy}</b> removed <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP_MESSAGES) {
      return generateStepMessageDeleteMessage(actionBy, before, auditData);
    }

    if (entityType === auditEntityTypes.SEQUENCE_SETTING) {
      return null;
    }

    if (entityType === "SequenceTag") {
      const tagName = before?.tag;
      if (tagName) {
        return `<b>${actionBy}</b> removed tag '<b>${tagName}</b>' from ${journeyReference}`;
      }
      return `<b>${actionBy}</b> removed tag from ${journeyReference}`;
    }

    const name = before?.name || before?.title || before?.subject;
    if (name) {
      return `<b>${actionBy}</b> deleted the ${featureNames.SEQUENCES} '<b>${name}</b>'`;
    }
    return `<b>${actionBy}</b> deleted ${journeyReference}`;
  } catch (error) {
    console.error("An error occurred while generating delete message: ", error);
    return null;
  }
}

function generateUpdateMessage(
  actionBy,
  entityName,
  before,
  after,
  entityType,
  sequenceName,
  auditData,
) {
  try {
    const changes = getChanges(before, after);

    if (changes.length === 0) {
      const journeyReference = getJourneyReference(sequenceName);
      return `<b>${actionBy}</b> updated ${journeyReference}`;
    }

    if (entityType === auditEntityTypes.SEQUENCE_SETTING) {
      return generateSequenceSettingMessages(
        actionBy,
        changes,
        sequenceName,
        before,
        after,
      );
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP) {
      return generateSequenceStepMessage(
        actionBy,
        changes,
        before,
        after,
        sequenceName,
        auditData,
      );
    }

    if (entityType === auditEntityTypes.SEQUENCE) {
      return generateSequenceMessage(actionBy, changes, sequenceName);
    }

    if (entityType === auditEntityTypes.SEQUENCE_STEP_MESSAGES) {
      return generateStepMessageUpdateMessage(
        actionBy,
        changes,
        before,
        after,
        auditData,
      );
    }

    if (entityType === auditEntityTypes.SEQUENCE_EXECUTION) {
      return generateSequenceExecutionUpdateMessage(
        actionBy,
        changes,
        before,
        after,
        auditData,
      );
    }

    if (entityType === "SequenceTag") {
      return generateSequenceTagUpdateMessage(
        actionBy,
        changes,
        before,
        after,
        sequenceName,
      );
    }

    if (changes.length === 1) {
      return generateSingleChangeMessage(
        actionBy,
        changes[0],
        entityType,
        sequenceName,
      );
    }

    return generateMultipleChangesMessage(
      actionBy,
      changes,
      entityType,
      sequenceName,
    );
  } catch (error) {
    console.error("An error occurred while generating update message: ", error);
    return null;
  }
}

function generateSequenceTagUpdateMessage(
  actionBy,
  changes,
  before,
  after,
  sequenceName,
) {
  try {
    const journeyReference = getJourneyReference(sequenceName);

    const isDeletionUpdate = changes.some(
      (change) =>
        change.field === "is_deleted" &&
        change.from === false &&
        change.to === true,
    );

    if (isDeletionUpdate) {
      const tagName = after?.tag || before?.tag;
      if (tagName) {
        return `<b>${actionBy}</b> removed tag '<b>${tagName}</b>' from ${journeyReference}`;
      }
      return `<b>${actionBy}</b> removed tag from ${journeyReference}`;
    }

    const tagChange = changes.find((change) => change.field === "tag");

    if (tagChange) {
      const { from, to } = tagChange;
      if (from && to) {
        return `<b>${actionBy}</b> changed tag from '<b>${from}</b>' to '<b>${to}</b>' in ${journeyReference}`;
      } else if (to) {
        return `<b>${actionBy}</b> set tag to '<b>${to}</b>' in ${journeyReference}`;
      }
    }

    return `<b>${actionBy}</b> updated tag in ${journeyReference}`;
  } catch (error) {
    console.error("Error generating sequence tag update message: ", error);
    return null;
  }
}

function generateSequenceExecutionUpdateMessage(
  actionBy,
  changes,
  before,
  after,
  auditData,
) {
  try {
    const statusChange = changes.find((change) => change.field === "status");

    if (statusChange) {
      const { from, to } = statusChange;

      if ((from === "PENDING" || from === "IN_PROGRESS") && to === "REMOVED") {
        const entityDetails = auditData?.entity_details;
        const entity = entityDetails?.entity;
        const entityType = entityDetails?.entity_type?.toLowerCase();
        const entityMapping = entityTypeMapping[entityType];
        const targetName = entityMapping ? entityMapping.singular : entityType;

        return `<b>${actionBy}</b> removed a <b>${targetName}</b> from the Sequence`;
      }
    }

    return null;
  } catch (error) {
    console.error(
      "Error generating sequence execution update message: ",
      error,
    );
    return null;
  }
}

async function getStepDetailsFromAssociatedEntities(auditData) {
  try {
    const associatedEntities = auditData?.associated_entities;
    const sequenceStepId = associatedEntities?.SequenceStep?.[0];

    if (sequenceStepId) {
      return {
        stepId: sequenceStepId,
        stepPosition: "unknown",
        stepType: "Step",
        automationType: null,
      };
    }

    return null;
  } catch (error) {
    console.error(
      "Error getting step details from associated entities: ",
      error,
    );
    return null;
  }
}

function generateStepMessageInsertMessage(actionBy, after, auditData) {
  try {
    const entityDetails = auditData?.entity_details;
    const sequenceStep = entityDetails?.sequence_step;

    if (sequenceStep) {
      const stepType =
        sequenceStep.step_type && sequenceStep.automation_type
          ? getStepTypeDisplayName(
              sequenceStep.step_type,
              sequenceStep.automation_type,
            )
          : null;
      const stepPosition =
        sequenceStep.position && sequenceStep.position > 0
          ? sequenceStep.position
          : null;

      if (stepType && stepPosition) {
        return `<b>${actionBy}</b> added Variant for the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
      }
    }

    const sequenceStepId = auditData?.associated_entities?.SequenceStep?.[0];
    if (sequenceStepId) {
      return `<b>${actionBy}</b> added Variant for Sequence Step`;
    }

    return `<b>${actionBy}</b> added Variant for Sequence Step`;
  } catch (error) {
    console.error("Error generating step message insert message: ", error);
    return null;
  }
}

function generateStepMessageDeleteMessage(actionBy, before, auditData) {
  try {
    const entityDetails = auditData?.entity_details;
    const sequenceStep = entityDetails?.sequence_step;

    if (sequenceStep) {
      const stepType =
        sequenceStep.step_type && sequenceStep.automation_type
          ? getStepTypeDisplayName(
              sequenceStep.step_type,
              sequenceStep.automation_type,
            )
          : null;
      const stepPosition =
        sequenceStep.position && sequenceStep.position > 0
          ? sequenceStep.position
          : null;

      if (stepType && stepPosition) {
        return `<b>${actionBy}</b> deleted Variant for the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
      }
    }

    const sequenceStepId = auditData?.associated_entities?.SequenceStep?.[0];
    if (sequenceStepId) {
      return `<b>${actionBy}</b> deleted Variant for Sequence Step`;
    }

    return `<b>${actionBy}</b> deleted Variant for Sequence Step`;
  } catch (error) {
    console.error("Error generating step message delete message: ", error);
    return null;
  }
}

function generateStepMessageUpdateMessage(
  actionBy,
  changes,
  before,
  after,
  auditData,
) {
  try {
    const entityDetails = auditData?.entity_details;
    const sequenceStep = entityDetails?.sequence_step;

    let stepType = null;
    let stepPosition = null;

    if (sequenceStep) {
      stepType =
        sequenceStep.step_type && sequenceStep.automation_type
          ? getStepTypeDisplayName(
              sequenceStep.step_type,
              sequenceStep.automation_type,
            )
          : null;
      stepPosition =
        sequenceStep.position && sequenceStep.position > 0
          ? sequenceStep.position
          : null;
    }

    const isDeletionUpdate = changes.some(
      (change) =>
        change.field === "is_deleted" &&
        change.from === false &&
        change.to === true,
    );

    if (isDeletionUpdate) {
      if (stepType && stepPosition) {
        return `<b>${actionBy}</b> deleted Variant for the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
      }
      return `<b>${actionBy}</b> deleted Variant for Sequence Step`;
    }

    const isActiveChange = changes.find(
      (change) => change.field === "is_active",
    );

    if (isActiveChange && changes.length === 1) {
      const { from, to } = isActiveChange;
      const action = to ? "enabled" : "disabled";

      if (stepType && stepPosition) {
        return `<b>${actionBy}</b> ${action} Variant for the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
      }
      return `<b>${actionBy}</b> ${action} Variant for Sequence Step`;
    }

    if (stepType && stepPosition) {
      return `<b>${actionBy}</b> edited Variant for the <b>${stepType}</b> in <b>step ${stepPosition}</b>`;
    }

    const sequenceStepId = auditData?.associated_entities?.SequenceStep?.[0];
    if (sequenceStepId) {
      return `<b>${actionBy}</b> edited Variant for Sequence Step`;
    }

    return `<b>${actionBy}</b> edited Variant for Sequence Step`;
  } catch (error) {
    console.error("Error generating step message update message: ", error);
    return null;
  }
}

function generateSequenceMessage(actionBy, changes, sequenceName) {
  try {
    const messages = [];
    const journeyReference = getJourneyReference(sequenceName);

    for (const change of changes) {
      const { field, from, to } = change;

      switch (field) {
        case "name":
          if (from && to) {
            messages.push(
              `<b>${actionBy}</b> changed the Sequence name from '<b>${from}</b>' to '<b>${to}</b>'`,
            );
          } else if (to) {
            messages.push(
              `<b>${actionBy}</b> changed the Sequence name to '<b>${to}</b>'`,
            );
          }
          break;
        case "status":
          if (to === "ACTIVE") {
            messages.push(`<b>${actionBy}</b> launched ${journeyReference}`);
          } else if (to === "INACTIVE" || to === "IN_ACTIVE") {
            messages.push(`<b>${actionBy}</b> paused ${journeyReference}`);
          } else if (to === "PAUSED") {
            messages.push(`<b>${actionBy}</b> paused ${journeyReference}`);
          } else if (to === "DRAFT") {
            messages.push(
              `<b>${actionBy}</b> set ${journeyReference} to draft`,
            );
          } else {
            const statusFrom = statusLabels[from] || from;
            const statusTo = statusLabels[to] || to;
            messages.push(
              `<b>${actionBy}</b> changed the ${journeyReference} status from '<b>${statusFrom}</b>' to '<b>${statusTo}</b>'`,
            );
          }
          break;
        case "permission":
          const fromLabel = getPermissionLabel(from);
          const toLabel = getPermissionLabel(to);
          if (from && to) {
            messages.push(
              `<b>${actionBy}</b> changed the permission from '<b>${fromLabel}</b>' to '<b>${toLabel}</b>'`,
            );
          } else if (to) {
            messages.push(
              `<b>${actionBy}</b> set permission to '<b>${toLabel}</b>'`,
            );
          }
          break;
        default:
          const fieldLabel = fieldLabels[field] || field;
          if (to && from) {
            messages.push(
              `<b>${actionBy}</b> changed the ${journeyReference} <b>${fieldLabel}</b> from '<b>${from}</b>' to '<b>${to}</b>'`,
            );
          } else if (to) {
            messages.push(
              `<b>${actionBy}</b> set ${journeyReference} <b>${fieldLabel}</b> to '<b>${to}</b>'`,
            );
          } else {
            messages.push(
              `<b>${actionBy}</b> removed ${journeyReference} <b>${fieldLabel}</b>`,
            );
          }
      }
    }

    return messages;
  } catch (error) {
    console.error(
      "An error occurred while generating sequence messages: ",
      error,
    );
    return null;
  }
}

function isBooleanField(field) {
  try {
    const booleanFields = [
      sequenceSettingFields.ENABLE_MAILBOX_ROTATION,
      sequenceSettingFields.ROUND_ROBIN_ENABLED,
      sequenceSettingFields.CALL_ROUND_ROBIN_ENABLED,
      sequenceSettingFields.ADD_UNSUBSCRIBE_LINK,
      sequenceSettingFields.SKIP_EMAIL_VALIDATION,
      sequenceSettingFields.CATCH_ALL_ENABLED,
      sequenceSettingFields.REMOVE_ON_MEETING_BOOKED,
      sequenceSettingFields.CALL_REPLY_EXIT_ENABLED,
      sequenceSettingFields.EXCLUDE_ADDED_ENTITIES,
      sequenceSettingFields.ENRICH_PHONE_BY_DEFAULT,
      sequenceSettingFields.EXCLUDE_PEOPLE_FROM_LISTS,
    ];
    return booleanFields.includes(field);
  } catch (error) {
    console.error("An error occurred while checking boolean field: ", error);
    return false;
  }
}

function getPermissionLabel(permissionValue) {
  try {
    const permission = permissionOptions.find(
      (option) => option.value === permissionValue,
    );
    return permission ? permission.label : permissionValue;
  } catch (error) {
    console.error("An error occurred while getting permission label: ", error);
    return permissionValue;
  }
}

function formatActiveDays(days) {
  try {
    if (!Array.isArray(days)) return days;
    return days.join(", ");
  } catch (error) {
    console.error("An error occurred while formatting active days: ", error);
    return days;
  }
}

function convertTo12HourFormat(time24) {
  try {
    if (!time24) return time24;

    const [hours, minutes] = time24.split(":");
    const hour12 = parseInt(hours);
    const ampm = hour12 >= 12 ? "pm" : "am";
    const displayHour = hour12 % 12 || 12;

    return `${displayHour}:${minutes}${ampm}`;
  } catch (error) {
    console.error("An error occurred while converting time format: ", error);
    return time24;
  }
}

function processDetailsFields(changes, before, after) {
  try {
    const processedChanges = [];
    const processedFields = new Set();

    for (const change of changes) {
      const { field } = change;

      if (processedFields.has(field)) continue;

      const detailsMapping = detailsFieldMapping[field];
      if (detailsMapping) {
        const detailsField = detailsMapping.detailsField;
        const hasDetailsChange = changes.find((c) => c.field === detailsField);

        if (hasDetailsChange) {
          const fromDetails = before?.[detailsField];
          const toDetails = after?.[detailsField];

          const enhancedChange = {
            ...change,
            fromDetails: fromDetails,
            toDetails: toDetails,
            detailsMapping: detailsMapping,
          };

          processedChanges.push(enhancedChange);
          processedFields.add(field);
          processedFields.add(detailsField);
          continue;
        }
      }

      if (!processedFields.has(field)) {
        processedChanges.push(change);
        processedFields.add(field);
      }
    }

    return processedChanges;
  } catch (error) {
    console.error("An error occurred while processing details fields: ", error);
    return changes;
  }
}

function generateSequenceSettingMessages(
  actionBy,
  changes,
  sequenceName,
  before = {},
  after = {},
) {
  try {
    const messages = [];
    const processedChanges = processDetailsFields(changes, before, after);

    for (const change of processedChanges) {
      const { field, from, to, fromDetails, toDetails, detailsMapping } =
        change;

      if (detailsMapping) {
        const fromName =
          fromDetails?.[detailsMapping.nameProperty] || "Unknown";
        const toName = toDetails?.[detailsMapping.nameProperty] || "Unknown";

        if (from && to && from !== to) {
          messages.push(
            `<b>${actionBy}</b> changed the ${detailsMapping.label} from '<b>${fromName}</b>' to '<b>${toName}</b>'`,
          );
        } else if (to && !from) {
          messages.push(
            `<b>${actionBy}</b> set ${detailsMapping.label} to '<b>${toName}</b>'`,
          );
        } else if (!to && from) {
          messages.push(
            `<b>${actionBy}</b> removed ${detailsMapping.label} '<b>${fromName}</b>'`,
          );
        }
        continue;
      }

      switch (field) {
        case sequenceSettingFields.TIMEZONE:
          if (from && to && from !== to) {
            messages.push(
              `<b>${actionBy}</b> changed the timezone from '<b>${from}</b>' to '<b>${to}</b>'`,
            );
          } else if (to && !from) {
            messages.push(
              `<b>${actionBy}</b> changed the timezone to '<b>${to}</b>'`,
            );
          }
          break;
        case sequenceSettingFields.START_TIME:
          if (from && to) {
            const fromTime = convertTo12HourFormat(from);
            const toTime = convertTo12HourFormat(to);
            if (fromTime !== toTime) {
              messages.push(
                `<b>${actionBy}</b> changed the start time from '<b>${fromTime}</b>' to '<b>${toTime}</b>'`,
              );
            }
          } else if (to && !from) {
            const toTime = convertTo12HourFormat(to);
            messages.push(
              `<b>${actionBy}</b> set active time to '<b>${toTime}</b>'`,
            );
          }
          break;
        case sequenceSettingFields.END_TIME:
          if (from && to) {
            const fromTime = convertTo12HourFormat(from);
            const toTime = convertTo12HourFormat(to);
            if (fromTime !== toTime) {
              messages.push(
                `<b>${actionBy}</b> changed the end time from '<b>${fromTime}</b>' to '<b>${toTime}</b>'`,
              );
            }
          } else if (to && !from) {
            const toTime = convertTo12HourFormat(to);
            messages.push(
              `<b>${actionBy}</b> set active time to '<b>${toTime}</b>'`,
            );
          }
          break;
        case "active_days":
          const fromDays = formatActiveDays(from);
          const toDays = formatActiveDays(to);
          if (from && to && JSON.stringify(from) !== JSON.stringify(to)) {
            messages.push(
              `<b>${actionBy}</b> changed the <b>Active Days</b> from '<b>${fromDays}</b>' to '<b>${toDays}</b>'`,
            );
          } else if (to && !from) {
            messages.push(
              `<b>${actionBy}</b> set <b>Active Days</b> to '<b>${toDays}</b>'`,
            );
          }
          break;
        case sequenceSettingFields.ENABLE_MAILBOX_ROTATION:
          if (from !== to) {
            const rotationStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${rotationStatus} 'Mailbox rotation for personal mailboxes'`,
            );
          }
          break;
        case sequenceSettingFields.ROUND_ROBIN_ENABLED:
          if (from !== to) {
            const roundRobinStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${roundRobinStatus} 'Round-robin assignment'`,
            );
          }
          break;
        case sequenceSettingFields.CALL_ROUND_ROBIN_ENABLED:
          if (from !== to) {
            const callRoundRobinStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${callRoundRobinStatus} 'Phone call to team members via round robin'`,
            );
          }
          break;
        case sequenceSettingFields.ADD_UNSUBSCRIBE_LINK:
          if (from !== to) {
            const unsubscribeStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${unsubscribeStatus} the 'Unsubscribe link'`,
            );
          }
          break;
        case sequenceSettingFields.SKIP_EMAIL_VALIDATION:
          if (from !== to) {
            const validationStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${validationStatus} 'Email validation skipping'`,
            );
          }
          break;
        case sequenceSettingFields.CATCH_ALL_ENABLED:
          if (from !== to) {
            const catchAllStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${catchAllStatus} 'Catch-all emails exclusion'`,
            );
          }
          break;
        case sequenceSettingFields.REMOVE_ON_MEETING_BOOKED:
          if (from !== to) {
            const meetingStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${meetingStatus} 'Marking contact as finished when meeting is booked'`,
            );
          }
          break;
        case sequenceSettingFields.CALL_REPLY_EXIT_ENABLED:
          if (from !== to) {
            const callExitStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${callExitStatus} 'Marking contact as finished when call is connected'`,
            );
          }
          break;
        case sequenceSettingFields.EXCLUDE_ADDED_ENTITIES:
          if (from !== to) {
            const excludeStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${excludeStatus} 'Exclusion of contacts/leads added to other ${pluralFeatureNames.SEQUENCES}'`,
            );
          }
          break;
        case sequenceSettingFields.ENRICH_PHONE_BY_DEFAULT:
          if (from !== to) {
            const enrichStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${enrichStatus} 'Phone number enrichment'`,
            );
          }
          break;
        case sequenceSettingFields.EXCLUDE_CRM_LISTS:
          if (from && to && JSON.stringify(from) !== JSON.stringify(to)) {
            messages.push(`<b>${actionBy}</b> updated 'Excluded list'`);
          }
          break;
        case sequenceSettingFields.EXCLUDE_PEOPLE_FROM_LISTS:
          if (from !== to) {
            const excludePeopleStatus = to ? "enabled" : "disabled";
            messages.push(
              `<b>${actionBy}</b> ${excludePeopleStatus} 'Exclude people from lists'`,
            );
          }
          break;
        case sequenceSettingFields.UNSUBSCRIBE_MESSAGE:
          if (from !== to) {
            messages.push(`<b>${actionBy}</b> updated the unsubscribe message`);
          }
          break;
        case sequenceSettingFields.ROUND_ROBIN_TEAM_ID:
          if (from !== to) {
            messages.push(
              `<b>${actionBy}</b> updated round-robin team assignment`,
            );
          }
          break;
        case sequenceSettingFields.CALL_ROUND_ROBIN_TEAM_ID:
          if (from !== to) {
            messages.push(
              `<b>${actionBy}</b> updated call round-robin team assignment`,
            );
          }
          break;
        default:
          const fieldLabel = fieldLabels[field] || field;
          if (isBooleanField(field)) {
            if (from !== to) {
              const status = to ? "enabled" : "disabled";
              messages.push(`<b>${actionBy}</b> ${status} '${fieldLabel}'`);
            }
          } else if (to && from && from !== to) {
            messages.push(
              `<b>${actionBy}</b> changed the <b>${fieldLabel}</b> from '<b>${from}</b>' to '<b>${to}</b>'`,
            );
          } else if (to && !from) {
            messages.push(
              `<b>${actionBy}</b> set <b>${fieldLabel}</b> to '<b>${to}</b>'`,
            );
          } else if (!to && from) {
            messages.push(`<b>${actionBy}</b> removed <b>${fieldLabel}</b>`);
          }
      }
    }

    return messages.length > 0 ? messages : null;
  } catch (error) {
    console.error(
      "An error occurred while generating sequence setting messages: ",
      error,
    );
    return null;
  }
}

function generateSequenceStepMessage(
  actionBy,
  changes,
  before,
  after,
  sequenceName,
  auditData,
) {
  try {
    const messages = [];

    const isDeletionUpdate = changes.some(
      (change) =>
        change.field === "is_deleted" &&
        change.from === false &&
        change.to === true,
    );

    if (isDeletionUpdate) {
      const entityDetails = auditData?.entity_details;
      const stepType =
        entityDetails?.step_type && entityDetails?.automation_type
          ? getStepTypeDisplayName(
              entityDetails.step_type,
              entityDetails.automation_type,
            )
          : before?.step_type && before?.automation_type
            ? getStepTypeDisplayName(before.step_type, before.automation_type)
            : null;

      const stepPosition =
        entityDetails?.position && entityDetails.position > 0
          ? entityDetails.position
          : before?.position && before.position > 0
            ? before.position
            : null;

      if (stepType && stepPosition) {
        messages.push(
          `<b>${actionBy}</b> deleted the <b>${stepType}</b> in <b>step ${stepPosition}</b>`,
        );
      } else {
        messages.push(`<b>${actionBy}</b> deleted Sequence Step`);
      }
      return messages;
    }

    for (const change of changes) {
      const { field, from, to } = change;

      switch (field) {
        case fieldLabels.position:
          if (from && to && from !== to && from > 0 && to > 0) {
            const entityDetails = auditData?.entity_details;
            const stepType =
              entityDetails?.step_type && entityDetails?.automation_type
                ? getStepTypeDisplayName(
                    entityDetails.step_type,
                    entityDetails.automation_type,
                  )
                : getStepTypeDisplayName(
                    after?.step_type || before?.step_type,
                    after?.automation_type || before?.automation_type,
                  );

            if (stepType && stepType !== "unknown") {
              messages.push(
                `<b>${actionBy}</b> changed the <b>${stepType}</b> from <b>step ${from}</b> to <b>step ${to}</b>`,
              );
            } else {
              messages.push(
                `<b>${actionBy}</b> changed the Sequence Step from <b>position ${from}</b> to <b>position ${to}</b>`,
              );
            }
          }
          break;
        case "step_type":
          const newStepType = getStepTypeDisplayName(
            to,
            after?.automation_type,
          );
          const changeStepPosition = after?.position || before?.position;
          if (newStepType && changeStepPosition && changeStepPosition > 0) {
            messages.push(
              `<b>${actionBy}</b> changed the <b>step ${changeStepPosition}</b> to <b>${newStepType}</b>`,
            );
          } else {
            messages.push(`<b>${actionBy}</b> edited Sequence Step`);
          }
          break;
        default:
          const entityDetails = auditData?.entity_details;
          const stepType =
            entityDetails?.step_type && entityDetails?.automation_type
              ? getStepTypeDisplayName(
                  entityDetails.step_type,
                  entityDetails.automation_type,
                )
              : getStepTypeDisplayName(
                  after?.step_type || before?.step_type,
                  after?.automation_type || before?.automation_type,
                );

          const editStepPosition =
            entityDetails?.position && entityDetails.position > 0
              ? entityDetails.position
              : after?.position || before?.position;

          if (
            stepType &&
            stepType !== "unknown" &&
            editStepPosition &&
            editStepPosition > 0
          ) {
            messages.push(
              `<b>${actionBy}</b> edited the <b>${stepType}</b> in <b>step ${editStepPosition}</b>`,
            );
          } else {
            messages.push(`<b>${actionBy}</b> edited Sequence Step`);
          }
      }
    }

    return messages;
  } catch (error) {
    console.error(
      "An error occurred while generating sequence step message: ",
      error,
    );
    return null;
  }
}

function getChanges(before, after) {
  try {
    const changes = [];
    const allKeys = new Set([
      ...Object.keys(before || {}),
      ...Object.keys(after || {}),
    ]);

    for (const key of allKeys) {
      if (shouldIgnoreField(key)) continue;

      const beforeValue = before?.[key];
      const afterValue = after?.[key];

      if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
        changes.push({
          field: key,
          from: beforeValue,
          to: afterValue,
        });
      }
    }

    return changes;
  } catch (error) {
    console.error("An error occurred while getting changes: ", error);
    return [];
  }
}

function shouldIgnoreField(field) {
  try {
    return ignoredFields.includes(field);
  } catch (error) {
    console.error("An error occurred while checking ignored field: ", error);
    return false;
  }
}

function generateSingleChangeMessage(
  actionBy,
  change,
  entityType,
  sequenceName,
) {
  try {
    const { field, from, to } = change;
    const fieldLabel = fieldLabels[field] || field;
    const journeyReference = getJourneyReference(sequenceName);

    switch (field) {
      case fieldLabels.name:
      case fieldLabels.title:
      case fieldLabels.subject:
        if (from && to) {
          return `<b>${actionBy}</b> changed the Sequence name from '<b>${from}</b>' to '<b>${to}</b>'`;
        } else if (to) {
          return `<b>${actionBy}</b> changed the Sequence name to '<b>${to}</b>'`;
        }
        break;

      case fieldLabels.status:
        return generateStatusChangeMessage(actionBy, from, to, sequenceName);

      case "permission":
        const fromLabel = getPermissionLabel(from);
        const toLabel = getPermissionLabel(to);
        if (from && to) {
          return `<b>${actionBy}</b> changed the permission from '<b>${fromLabel}</b>' to '<b>${toLabel}</b>'`;
        } else if (to) {
          return `<b>${actionBy}</b> set permission to '<b>${toLabel}</b>'`;
        }
        return `<b>${actionBy}</b> updated permission`;

      default:
        if (to && from) {
          return `<b>${actionBy}</b> changed the <b>${fieldLabel}</b> from '<b>${from}</b>' to '<b>${to}</b>'`;
        } else if (to) {
          return `<b>${actionBy}</b> set <b>${fieldLabel}</b> to '<b>${to}</b>'`;
        } else {
          return `<b>${actionBy}</b> removed <b>${fieldLabel}</b>`;
        }
    }
  } catch (error) {
    console.error(
      "An error occurred while generating single change message: ",
      error,
    );
    return null;
  }
}

function generateStatusChangeMessage(actionBy, from, to, sequenceName) {
  try {
    const journeyReference = getJourneyReference(sequenceName);

    if (to === "ACTIVE") {
      return `<b>${actionBy}</b> launched ${journeyReference}`;
    } else if (to === "INACTIVE" || to === "IN_ACTIVE") {
      return `<b>${actionBy}</b> paused ${journeyReference}`;
    } else if (to === "PAUSED") {
      return `<b>${actionBy}</b> paused ${journeyReference}`;
    } else if (to === "DRAFT") {
      return `<b>${actionBy}</b> set ${journeyReference} to draft`;
    }

    const statusFrom = statusLabels[from] || from;
    const statusTo = statusLabels[to] || to;
    return `<b>${actionBy}</b> changed the ${journeyReference} status from '<b>${statusFrom}</b>' to '<b>${statusTo}</b>'`;
  } catch (error) {
    console.error(
      "An error occurred while generating status change message: ",
      error,
    );
    return null;
  }
}

function generateMultipleChangesMessage(
  actionBy,
  changes,
  entityType,
  sequenceName,
) {
  try {
    const changeDescriptions = changes.map((change) => {
      const fieldLabel = fieldLabels[change.field] || change.field;
      if (change.field === fieldLabels.status) {
        const statusTo = statusLabels[change.to] || change.to;
        return `status to '<b>${statusTo}</b>'`;
      }
      return `<b>${fieldLabel}</b> to '<b>${change.to}</b>'`;
    });

    if (changeDescriptions.length === 2) {
      return `<b>${actionBy}</b> changed the ${changeDescriptions.join(" and ")}`;
    }

    const lastChange = changeDescriptions.pop();
    return `<b>${actionBy}</b> changed the ${changeDescriptions.join(", ")} and ${lastChange}`;
  } catch (error) {
    console.error(
      "An error occurred while generating multiple changes message: ",
      error,
    );
    return null;
  }
}

function getEntityDisplayName(entityType) {
  try {
    return (
      entityDisplayNames[entityType?.toLowerCase()] || featureNames.SEQUENCES
    );
  } catch (error) {
    console.error(
      "An error occurred while getting entity display name: ",
      error,
    );
    return featureNames.SEQUENCES;
  }
}

function getStepTypeDisplayName(stepType, automationType) {
  try {
    const allOptions = [
      ...stepOptions.ai.options,
      ...stepOptions.automated.options,
      ...stepOptions.manual.options,
    ];

    const matchingOption = allOptions.find(
      (option) =>
        option.stepType === stepType &&
        option.automationType === automationType,
    );

    if (matchingOption) {
      return matchingOption.title;
    }

    const baseType =
      stepTypeDisplayNames[stepType] || stepType?.toLowerCase() || "unknown";

    if (automationType === "AI" && stepType === "EMAIL") {
      return `AI Email Agent`;
    } else if (automationType === "TASK" && stepType === "EMAIL") {
      return `Manual Email Task`;
    } else if (stepType === "EMAIL") {
      return `Email Step`;
    }

    return baseType;
  } catch (error) {
    console.error(
      "An error occurred while getting step type display name: ",
      error,
    );
    return "unknown";
  }
}

function getEntityGroupType(audits) {
  try {
    const entityTypes = new Set();
    audits.forEach((audit) => {
      let entityType;

      if (audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION) {
        entityType = audit.entity_details?.entity_type?.toLowerCase();
      } else {
        entityType =
          audit.after?.entity_type?.toLowerCase() ||
          audit.before?.entity_type?.toLowerCase();
      }

      if (
        entityType === auditEntities.LEAD ||
        entityType === auditEntities.CONTACT
      ) {
        entityTypes.add(entityType);
      }
    });

    return entityTypes.size > 0 ? entityTypeGrouping.CONTACT_LEAD : null;
  } catch (error) {
    console.error("An error occurred while getting entity group type: ", error);
    return null;
  }
}

export function generateCombinedMessage(
  actionBy,
  operationType,
  entityType,
  count,
  sequenceName = null,
  audits = [],
) {
  try {
    const config = operationConfig[operationType];

    if (!config) {
      return null;
    }

    const groupType = getEntityGroupType(audits);
    if (groupType === entityTypeGrouping.CONTACT_LEAD) {
      const entityMapping = entityTypeMapping[auditEntities.CONTACT];
      const entityName =
        count > 1 ? entityMapping.plural : entityMapping.singular;
      return `<b>${actionBy}</b> ${config.action} <b>${count} ${entityName}</b> in the Sequence`;
    }

    const entityMapping = entityTypeMapping[entityType?.toLowerCase()];
    if (!entityMapping) {
      return null;
    }

    const entityName =
      count > 1 ? entityMapping.plural : entityMapping.singular;
    return `<b>${actionBy}</b> ${config.action} <b>${count} ${entityName}</b> in the Sequence`;
  } catch (error) {
    console.error(
      "An error occurred while generating combined message: ",
      error,
    );
    return null;
  }
}

export function getEntityDetails(audits) {
  try {
    return audits.map((audit) => {
      if (audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION) {
        const entity = audit.entity_details?.entity;
        const entityType = audit.entity_details?.entity_type;

        if (entity) {
          const name =
            entity.first_name && entity.last_name
              ? `${entity.first_name} ${entity.last_name}`
              : entity.first_name ||
                entity.last_name ||
                `${entityType} ${entity.id}`;

          return {
            id: entity.id,
            type: entityType,
            name: name,
            email: entity.email || "",
            entityDetails: entity,
            first_name: entity.first_name || "",
            last_name: entity.last_name || "",
            profile_pic: entity.profile_pic || null,
          };
        }
      }

      const entityId = audit.after?.entity_id || audit.before?.entity_id;
      const entityType = audit.after?.entity_type || audit.before?.entity_type;
      const entityDetails = audit.after?.entity_details;

      let name = `${entityType} ${entityId}`;
      let email = "";

      if (entityDetails?.attributes?.name) {
        name =
          entityDetails.attributes.name.full_name ||
          `${entityDetails.attributes.name.first_name || ""} ${entityDetails.attributes.name.last_name || ""}`.trim();
      }

      if (entityDetails?.attributes?.email?.value) {
        email = entityDetails.attributes.email.value;
      }

      return {
        id: entityId,
        type: entityType,
        name: name,
        email: email,
        entityDetails: entityDetails,
        first_name: entityDetails?.attributes?.name?.first_name || "",
        last_name: entityDetails?.attributes?.name?.last_name || "",
        profile_pic: entityDetails?.attributes?.profile_pic?.value || null,
      };
    });
  } catch (error) {
    console.error("An error occurred while getting entity details: ", error);
    return [];
  }
}
